/**
 * `/$tenantSlug/settings/team` — Team management (Wave 2, slice 1).
 *
 * Per ADR-0079 (DB-backed single-use invitation tokens, link-delivered in
 * v1 — no email module yet) + ADR-0080 (users are deactivated, never
 * hard-deleted). The first user-management surface: list members, invite a
 * new member by email, deactivate / reactivate a member, change a member's
 * roles, and revoke a pending invitation.
 *
 * Archetype: "config" (design-intent §A) — header only, NO hero/gradient.
 *
 * Permissions:
 *   - `shell.team.view` gates the whole page (RequirePermission).
 *   - `shell.team.edit` unlocks the mutating affordances. View-only members
 *     see the lists but no Invite button, no row actions, and a read-only
 *     note (mirrors organization.tsx).
 *
 * Accessibility (design-intent §G):
 *   - Status (active / deactivated) is an AA token pair + glyph + text via
 *     StatusGlyph — never colour-only (§G.7, WCAG 1.4.1).
 *   - A role=status live region announces every mutation ("Member
 *     deactivated", "Invitation created") WITHOUT stealing focus (§G.8).
 *   - On a failed mutation the optimistic change rolls back and the error
 *     surfaces in a role=alert that receives focus (§G — focus only on fail).
 *   - Controls are ≥40px (Button md) and labels never fall below 12px (§F).
 *
 * Backend contract (consumed, built in a parallel worktree):
 *   GET    /api/shell/team/members        -> TeamMember[]
 *   GET    /api/shell/team/invitations    -> Invitation[]
 *   POST   /api/shell/team/invitations    {email, roles} -> {invitation_id, accept_link}
 *   DELETE /api/shell/team/invitations/{id}                -> 204
 *   POST   /api/shell/team/members/{id}/deactivate         -> 204
 *   POST   /api/shell/team/members/{id}/reactivate         -> 204
 *   PUT    /api/shell/team/members/{id}/roles {roles}      -> 204
 *   GET    /api/shell/rbac/roles          -> Role[]  (the role picker source)
 */

import { usePermission } from "@ken/auth";
import { FormField } from "@ken/forms";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  StatusGlyph,
} from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, UserPlus } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from "react";

import { NamedRolePicker } from "../components/NamedRolePicker";
import { ASSIGNABLE_ROLE_ORDER, roleLabel } from "../lib/roles";

export const Route = createFileRoute("/$tenantSlug/settings/team")({
  component: TeamSettingsRoute,
});

// ---------------------------------------------------------------------------
// Backend contract (ADR-0079/0080) — consumed, not built here.
// ---------------------------------------------------------------------------

interface TeamMember {
  user_id: string;
  email: string;
  display_name: string | null;
  roles: string[];
  deactivated_at: string | null;
  created_at: string;
  /** 'member' | 'advisor' — multi_company_access (ADR-0089/0090). */
  membership_type?: string;
}

interface Invitation {
  invitation_id: string;
  email: string;
  roles: string[];
  status: string;
  expires_at: string;
  created_at: string;
}

interface Role {
  id: string;
  role_name: string;
  /** named_roles (ADR-0097): the curated description; the picker consumes it. */
  description: string | null;
  /** named_roles: only `is_system` roles (minus advisor) are assignable here. */
  is_system: boolean;
}

/**
 * Default selected role for a fresh invite / a member with no recognized role.
 * `read_only` is the safest floor (view everything, change nothing).
 */
const DEFAULT_ROLE = "read_only";

/** The member's first assignable system role, or the default floor. */
function memberRole(member: TeamMember): string {
  const found = member.roles.find((r) => ASSIGNABLE_ROLE_ORDER.includes(r));
  return found ?? DEFAULT_ROLE;
}

interface InviteResponse {
  invitation_id: string;
  accept_link: string;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function TeamSettingsRoute(): ReactElement {
  const canView = usePermission("shell.team.view");
  const canEdit = usePermission("shell.team.edit");
  const readOnly = canView && !canEdit;

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const errorRef = useRef<HTMLDivElement | null>(null);

  // Load members + invitations + roles.
  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [membersRes, invitesRes, rolesRes] = await Promise.all([
          fetch("/api/shell/team/members", { credentials: "include" }),
          fetch("/api/shell/team/invitations", { credentials: "include" }),
          fetch("/api/shell/rbac/roles", { credentials: "include" }),
        ]);
        if (cancelled) return;
        if (membersRes.status === 403 || invitesRes.status === 403) {
          setError("You don't have permission to view the team.");
          return;
        }
        if (!membersRes.ok || !invitesRes.ok) {
          setError(
            `Couldn't load the team (status ${
              membersRes.ok ? invitesRes.status : membersRes.status
            }).`,
          );
          return;
        }
        setMembers((await membersRes.json()) as TeamMember[]);
        setInvitations((await invitesRes.json()) as Invitation[]);
        if (rolesRes.ok) {
          if (!cancelled) setRoles((await rolesRes.json()) as Role[]);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // On error, move focus to the alert (§G — focus on fail only).
  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  // -- Mutations (optimistic + announce + rollback) -----------------------

  async function setDeactivated(member: TeamMember, deactivate: boolean): Promise<void> {
    if (!canEdit || busyUserId) return;
    setBusyUserId(member.user_id);
    setError(null);
    const prev = members;
    const optimisticAt = deactivate ? new Date().toISOString() : null;
    setMembers((ms) =>
      ms.map((m) =>
        m.user_id === member.user_id ? { ...m, deactivated_at: optimisticAt } : m,
      ),
    );
    try {
      const res = await fetch(
        `/api/shell/team/members/${member.user_id}/${deactivate ? "deactivate" : "reactivate"}`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        setMembers(prev);
        setError(
          res.status === 403
            ? "You don't have permission to change members."
            : `Couldn't update the member (status ${res.status}).`,
        );
        return;
      }
      setStatusMessage(deactivate ? "Member deactivated." : "Member reactivated.");
    } catch (err) {
      setMembers(prev);
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setBusyUserId(null);
    }
  }

  async function revokeInvitation(invite: Invitation): Promise<void> {
    if (!canEdit || busyInviteId) return;
    setBusyInviteId(invite.invitation_id);
    setError(null);
    const prev = invitations;
    setInvitations((is) => is.filter((i) => i.invitation_id !== invite.invitation_id));
    try {
      const res = await fetch(`/api/shell/team/invitations/${invite.invitation_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        setInvitations(prev);
        setError(
          res.status === 403
            ? "You don't have permission to revoke invitations."
            : `Couldn't revoke the invitation (status ${res.status}).`,
        );
        return;
      }
      setStatusMessage("Invitation revoked.");
    } catch (err) {
      setInvitations(prev);
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setBusyInviteId(null);
    }
  }

  function onInvited(invite: Invitation): void {
    setInvitations((is) => [invite, ...is]);
    setStatusMessage("Invitation created.");
  }

  // Optimistic role swap to a single named role (named_roles — ADR-0097).
  // The change-role dialog calls this; it updates the member's chip in place
  // and announces in the live region. On failure it rolls back and surfaces
  // the role=alert.
  async function changeRole(member: TeamMember, newRole: string): Promise<void> {
    if (!canEdit) return;
    setError(null);
    const prev = members;
    setMembers((ms) =>
      ms.map((m) =>
        m.user_id === member.user_id ? { ...m, roles: [newRole] } : m,
      ),
    );
    const res = await fetch(`/api/shell/team/members/${member.user_id}/roles`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roles: [newRole] }),
    });
    if (!res.ok) {
      setMembers(prev);
      throw new Error(
        res.status === 403
          ? "You don't have permission to change roles."
          : `Couldn't change the role (status ${res.status}).`,
      );
    }
    setStatusMessage(`Role changed to ${roleLabel(newRole)}.`);
  }

  const byName = (a: TeamMember, b: TeamMember): number =>
    (a.display_name ?? a.email).localeCompare(b.display_name ?? b.email);

  // Members and advisors are the same `users` rows split by membership_type
  // (multi_company_access). Advisors get their own section; the Members
  // section lists everyone who isn't an advisor.
  const sortedMembers = useMemo(
    () =>
      [...members]
        .filter((m) => m.membership_type !== "advisor")
        .sort(byName),
    [members],
  );
  const advisors = useMemo(
    () =>
      [...members]
        .filter((m) => m.membership_type === "advisor")
        .sort(byName),
    [members],
  );

  if (!canView) {
    return (
      <Card surface="glass-strong">
        <CardContent>
          <p className="text-sm text-[var(--color-text-soft)]">
            You don&apos;t have permission to view the team.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="shell-settings-team-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
            Tenant · Settings
          </div>
          <h1 className="text-[26px] font-bold tracking-tight">Team</h1>
          <p className="text-sm text-[var(--color-text-soft)] mt-1">
            The people who can sign in to this workspace, the roles they hold,
            and any invitations waiting to be accepted.
          </p>
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="primary"
            size="md"
            data-testid="shell-settings-team-invite-button"
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus size={16} aria-hidden="true" />
            Invite member
          </Button>
        )}
      </header>

      {/* Live region — announces mutations without stealing focus (§G.8). */}
      <div
        data-testid="shell-settings-team-live"
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {statusMessage}
      </div>

      {error !== null && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          data-testid="shell-settings-team-error"
          className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
        >
          {error}
        </div>
      )}

      {readOnly && (
        <div
          data-testid="shell-settings-team-readonly-note"
          className="text-[13px] text-[var(--color-text-soft)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-surface-soft)] border border-[var(--color-border-hairline)]"
        >
          You can view the team but not change it. Ask an owner or admin to
          invite, deactivate, or re-role members.
        </div>
      )}

      {loading ? (
        <div data-testid="shell-settings-team-loading" className="flex flex-col gap-3">
          <Skeleton className="h-40 w-full rounded-[var(--radius-md)]" />
          <Skeleton className="h-24 w-full rounded-[var(--radius-md)]" />
        </div>
      ) : (
        <>
          {/* Members section */}
          <section aria-labelledby="team-members-heading" className="flex flex-col gap-3">
            <h2 id="team-members-heading" className="text-[18px] font-semibold">
              Members
            </h2>
            {sortedMembers.length === 0 ? (
              <Card surface="glass-strong" padding="lg">
                <CardContent>
                  <div
                    data-testid="shell-settings-team-empty"
                    className="text-sm text-[var(--color-text-soft)] text-center py-8"
                  >
                    No team members yet.
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="flex flex-col gap-2" data-testid="shell-settings-team-members-list">
                {sortedMembers.map((m) => (
                  <MemberRow
                    key={m.user_id}
                    member={m}
                    roles={roles}
                    canEdit={canEdit}
                    busy={busyUserId === m.user_id}
                    onToggleActive={(deactivate) => void setDeactivated(m, deactivate)}
                    onChangeRole={(newRole) => changeRole(m, newRole)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Advisors — external accountants (membership_type='advisor').
              Hidden entirely when none, like Pending invitations. */}
          {advisors.length > 0 && (
            <section
              aria-labelledby="team-advisors-heading"
              className="flex flex-col gap-3"
              data-testid="shell-settings-team-advisors"
            >
              <h2 id="team-advisors-heading" className="text-[18px] font-semibold">
                Advisors
              </h2>
              <p className="text-sm text-[var(--color-text-soft)] -mt-1">
                External accountants with read-only access to the books. They
                can&apos;t change settings, billing, or approve actions.
              </p>
              <div
                className="flex flex-col gap-2"
                data-testid="shell-settings-team-advisors-list"
              >
                {advisors.map((m) => (
                  <MemberRow
                    key={m.user_id}
                    member={m}
                    roles={roles}
                    canEdit={canEdit}
                    busy={busyUserId === m.user_id}
                    onToggleActive={(deactivate) => void setDeactivated(m, deactivate)}
                    onChangeRole={(newRole) => changeRole(m, newRole)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Pending invitations — hidden entirely when none. */}
          {invitations.length > 0 && (
            <section
              aria-labelledby="team-invitations-heading"
              className="flex flex-col gap-3"
              data-testid="shell-settings-team-invitations"
            >
              <h2 id="team-invitations-heading" className="text-[18px] font-semibold">
                Pending invitations
              </h2>
              <div className="flex flex-col gap-2">
                {invitations.map((inv) => (
                  <InvitationRow
                    key={inv.invitation_id}
                    invitation={inv}
                    canEdit={canEdit}
                    busy={busyInviteId === inv.invitation_id}
                    onRevoke={() => void revokeInvitation(inv)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {canEdit && (
        <InviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          roles={roles}
          onInvited={onInvited}
          onAnnounce={setStatusMessage}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member row
// ---------------------------------------------------------------------------

function MemberRow({
  member,
  roles,
  canEdit,
  busy,
  onToggleActive,
  onChangeRole,
}: {
  member: TeamMember;
  roles: Role[];
  canEdit: boolean;
  busy: boolean;
  onToggleActive: (deactivate: boolean) => void;
  onChangeRole: (newRole: string) => Promise<void>;
}): ReactElement {
  const deactivated = member.deactivated_at !== null;
  // Status as AA token pair + glyph + text — never colour-only (§G.7).
  const tone: "positive" | "inert" = deactivated ? "inert" : "positive";
  const label = deactivated ? "Deactivated" : "Active";
  const isAdvisor = member.membership_type === "advisor";
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);

  return (
    <Card
      surface="glass-strong"
      data-testid={`shell-settings-team-member-${member.email}`}
    >
      <CardContent>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-[var(--color-text-strong)]">
                {member.display_name ?? member.email}
              </span>
              <StatusBadge tone={tone} label={label} />
              {isAdvisor && (
                <Chip tone="amber" data-testid={`${member.email}-advisor-chip`}>
                  Advisor
                </Chip>
              )}
            </div>
            <div className="text-sm text-[var(--color-text-soft)] mt-0.5">
              {member.email}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              {member.roles.length === 0 ? (
                <span className="text-[12px] text-[var(--color-text-faint)]">
                  No roles
                </span>
              ) : (
                member.roles.map((r) => (
                  <Chip key={r} tone="brand">
                    {roleLabel(r)}
                  </Chip>
                ))
              )}
            </div>
            <div className="text-[12px] text-[var(--color-text-faint)] mt-1.5">
              Added {fmtDate(member.created_at)}
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2 shrink-0">
              {/* Advisors hold the fixed `advisor` role server-side; they have
                  no assignable named role, so the change-role affordance is
                  gated to non-advisor members (named_roles — ADR-0097/0090). */}
              {!isAdvisor && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`shell-settings-team-member-${member.email}-role`}
                  disabled={busy}
                  onClick={() => setRoleDialogOpen(true)}
                >
                  Change role
                </Button>
              )}
              {deactivated ? (
                <Button
                  type="button"
                  variant="solid"
                  size="sm"
                  data-testid={`shell-settings-team-member-${member.email}-reactivate`}
                  disabled={busy}
                  onClick={() => onToggleActive(false)}
                >
                  {busy ? "Working…" : "Reactivate"}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`shell-settings-team-member-${member.email}-deactivate`}
                  disabled={busy}
                  onClick={() => onToggleActive(true)}
                >
                  {busy ? "Working…" : "Deactivate"}
                </Button>
              )}
            </div>
          )}
        </div>
        {canEdit && !isAdvisor && (
          <ChangeRoleDialog
            open={roleDialogOpen}
            onOpenChange={setRoleDialogOpen}
            member={member}
            roles={roles}
            onChangeRole={onChangeRole}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Change-role dialog (named_roles — ADR-0097)
// ---------------------------------------------------------------------------

/**
 * In-place role change for one member. Replaces the old "Change roles" button
 * that navigated away to the per-user override page. A Dialog containing the
 * shared NamedRolePicker, pre-selected to the member's current role, PUTs
 * `{ roles: [newRole] }` to /api/shell/team/members/{id}/roles. The parent's
 * optimistic update swaps the member's chip and announces in the page live
 * region; on failure the parent rolls back and this dialog focuses its own
 * role=alert (focus-on-fail).
 */
function ChangeRoleDialog({
  open,
  onOpenChange,
  member,
  roles,
  onChangeRole,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: TeamMember;
  roles: Role[];
  onChangeRole: (newRole: string) => Promise<void>;
}): ReactElement {
  const current = memberRole(member);
  const currentRef = useRef(current);
  currentRef.current = current;
  const [selectedRole, setSelectedRole] = useState<string>(current);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Re-seed the picker to the member's current role each time the dialog
  // OPENS (so the radiogroup focus lands on the current role — a11y contract).
  // Depend only on `open`: the member's `current` role flips mid-submit (the
  // parent's optimistic update + rollback), and re-running this on every
  // `current` change would wipe the submit error before it could render.
  useEffect(() => {
    if (open) {
      setSelectedRole(currentRef.current);
      setFieldError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus the alert on failure (§G — focus on fail only).
  useEffect(() => {
    if (fieldError && errorRef.current) errorRef.current.focus();
  }, [fieldError]);

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setSubmitting(false);
      setFieldError(null);
    }
    onOpenChange(next);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFieldError(null);
    if (selectedRole === current) {
      onOpenChange(false);
      return;
    }
    setSubmitting(true);
    try {
      await onChangeRole(selectedRole);
      onOpenChange(false);
    } catch (err) {
      setFieldError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="shell-settings-team-changerole-dialog">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>
            Choose the role for {member.display_name ?? member.email}. Each
            role is a fixed bundle of permissions.
          </DialogDescription>
        </DialogHeader>

        <form
          data-testid="shell-settings-team-changerole-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => void onSubmit(e)}
        >
          {fieldError !== null && (
            <div
              ref={errorRef}
              tabIndex={-1}
              role="alert"
              data-testid="shell-settings-team-changerole-error"
              className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
            >
              {fieldError}
            </div>
          )}

          <NamedRolePicker
            roles={roles}
            value={selectedRole}
            onChange={setSelectedRole}
            testIdPrefix="shell-settings-team-changerole"
            name={`changerole-${member.user_id}`}
          />

          <DialogFooter>
            <Button
              type="submit"
              variant="primary"
              size="md"
              data-testid="shell-settings-team-changerole-submit"
              disabled={submitting}
            >
              {submitting ? "Saving…" : "Save role"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Invitation row
// ---------------------------------------------------------------------------

function InvitationRow({
  invitation,
  canEdit,
  busy,
  onRevoke,
}: {
  invitation: Invitation;
  canEdit: boolean;
  busy: boolean;
  onRevoke: () => void;
}): ReactElement {
  return (
    <Card
      surface="glass-strong"
      data-testid={`shell-settings-team-invitation-${invitation.email}`}
    >
      <CardContent>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-[var(--color-text-strong)]">
                {invitation.email}
              </span>
              <StatusBadge tone="attention" label="Pending" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
              {invitation.roles.map((r) => (
                <Chip key={r} tone="brand">
                  {roleLabel(r)}
                </Chip>
              ))}
            </div>
            <div className="text-[12px] text-[var(--color-text-faint)] mt-1.5">
              Expires {fmtDate(invitation.expires_at)}
            </div>
          </div>
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid={`shell-settings-team-invitation-${invitation.email}-revoke`}
              disabled={busy}
              onClick={onRevoke}
            >
              {busy ? "Revoking…" : "Revoke"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Status badge: AA token pair + glyph + visible text (§G.7). */
function StatusBadge({
  tone,
  label,
}: {
  tone: "positive" | "attention" | "inert";
  label: string;
}): ReactElement {
  const bg =
    tone === "positive"
      ? "--color-status-active-bg"
      : tone === "attention"
        ? "--color-status-trial-bg"
        : "--color-status-archived-bg";
  const text =
    tone === "positive"
      ? "--color-status-active-text"
      : tone === "attention"
        ? "--color-status-trial-text"
        : "--color-status-archived-text";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--radius-full)] px-2 py-0.5 text-[12px] font-medium"
      style={{ backgroundColor: `var(${bg})`, color: `var(${text})` }}
    >
      <StatusGlyph tone={tone} size={11} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Invite dialog
// ---------------------------------------------------------------------------

function InviteDialog({
  open,
  onOpenChange,
  roles,
  onInvited,
  onAnnounce,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
  onInvited: (invite: Invitation) => void;
  /** Announce into the page live region (advisor-toggle a11y contract). */
  onAnnounce: (message: string) => void;
}): ReactElement {
  const [email, setEmail] = useState("");
  const [membershipType, setMembershipType] = useState<"member" | "advisor">(
    "member",
  );
  const [selectedRole, setSelectedRole] = useState<string>(DEFAULT_ROLE);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [acceptLink, setAcceptLink] = useState<string | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string>("");
  const [copied, setCopied] = useState(false);

  const isAdvisor = membershipType === "advisor";

  // Reset all state when the dialog closes.
  function reset(): void {
    setEmail("");
    setMembershipType("member");
    setSelectedRole(DEFAULT_ROLE);
    setSubmitting(false);
    setFieldError(null);
    setAcceptLink(null);
    setInvitedEmail("");
    setCopied(false);
  }

  function handleOpenChange(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  // Flipping the access type hides/shows the role picker; announce the change
  // (and the picker's removal) into the page live region — today it was a
  // silent DOM change (named_roles a11y contract).
  function selectMembership(next: "member" | "advisor"): void {
    setMembershipType(next);
    onAnnounce(
      next === "advisor"
        ? "Advisor selected. Role is set automatically and the role picker is hidden."
        : "Team member selected. Choose a role.",
    );
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setFieldError(null);
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFieldError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    // Advisor invites carry no roles — the backend forces the fixed system
    // `advisor` role and ignores any passed roles (multi_company_access).
    // Members carry exactly one named role (named_roles — ADR-0097).
    const rolesToSend = isAdvisor ? [] : [selectedRole];
    try {
      const res = await fetch("/api/shell/team/invitations", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          roles: rolesToSend,
          membership_type: membershipType,
        }),
      });
      if (res.status === 403) {
        setFieldError("You don't have permission to invite members.");
        return;
      }
      if (res.status === 409) {
        setFieldError("There's already a pending invitation for this email.");
        return;
      }
      if (!res.ok) {
        setFieldError(`Couldn't create the invitation (status ${res.status}).`);
        return;
      }
      const body = (await res.json()) as InviteResponse;
      // Build the full copyable URL (the backend returns a relative path).
      const full = body.accept_link.startsWith("http")
        ? body.accept_link
        : `${window.location.origin}${body.accept_link}`;
      setAcceptLink(full);
      setInvitedEmail(trimmed);
      onInvited({
        invitation_id: body.invitation_id,
        email: trimmed,
        roles: rolesToSend,
        status: "pending",
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      setFieldError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (!acceptLink) return;
    try {
      await navigator.clipboard.writeText(acceptLink);
      setCopied(true);
    } catch {
      // Clipboard may be unavailable; the link is selectable as a fallback.
      setCopied(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="shell-settings-team-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite to this company</DialogTitle>
          <DialogDescription>
            {acceptLink
              ? "The invitation is ready. Send the link below to your invitee."
              : isAdvisor
                ? "Invite an external accountant with read-only access to the books."
                : "Enter their email and choose the roles they should start with."}
          </DialogDescription>
        </DialogHeader>

        {acceptLink ? (
          <div className="flex flex-col gap-3">
            <div
              data-testid="shell-settings-team-invite-link"
              className="select-all break-all rounded-[var(--radius-sm)] border border-[var(--color-border-hairline)] bg-[var(--color-surface-soft)] px-3 py-2 text-[13px] font-mono text-[var(--color-text-strong)]"
            >
              {acceptLink}
            </div>
            <p className="text-[13px] text-[var(--color-text-soft)]">
              Email delivery isn&apos;t set up yet — copy this link and send it
              to {invitedEmail} yourself. It expires in 7 days.
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="primary"
                size="md"
                data-testid="shell-settings-team-invite-copy"
                onClick={() => void copyLink()}
              >
                {copied ? (
                  <>
                    <Check size={16} aria-hidden="true" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} aria-hidden="true" />
                    Copy link
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            data-testid="shell-settings-team-invite-form"
            className="flex flex-col gap-4"
            onSubmit={(e) => void onSubmit(e)}
          >
            {/* Access type — a real radiogroup (multi_company_access). Choosing
                advisor hides the role checklist (the advisor role is fixed
                server-side) and sends membership_type='advisor'. */}
            <fieldset
              role="radiogroup"
              aria-label="Access type"
              className="border-0 p-0 m-0"
              data-testid="shell-settings-team-invite-membership"
            >
              <legend className="text-[12px] font-semibold text-[var(--color-text-strong)] mb-2">
                Access type
              </legend>
              <div className="flex flex-col gap-1.5">
                <label className="flex items-start gap-2 px-2 py-1.5 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-soft)] cursor-pointer">
                  <input
                    type="radio"
                    name="membership_type"
                    checked={membershipType === "member"}
                    onChange={() => selectMembership("member")}
                    data-testid="shell-settings-team-invite-membership-member"
                    className="accent-[var(--color-brand)] w-4 h-4 shrink-0 mt-0.5"
                  />
                  <span>
                    <span className="text-sm text-[var(--color-text-strong)]">
                      Team member
                    </span>
                    <span className="block text-[12px] text-[var(--color-text-soft)]">
                      Works inside this company; you choose their roles.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 px-2 py-1.5 rounded-[var(--radius-sm)] hover:bg-[var(--color-surface-soft)] cursor-pointer">
                  <input
                    type="radio"
                    name="membership_type"
                    checked={membershipType === "advisor"}
                    onChange={() => selectMembership("advisor")}
                    data-testid="shell-settings-team-invite-membership-advisor"
                    className="accent-[var(--color-brand)] w-4 h-4 shrink-0 mt-0.5"
                  />
                  <span>
                    <span className="text-sm text-[var(--color-text-strong)]">
                      External accountant (advisor)
                    </span>
                    <span className="block text-[12px] text-[var(--color-text-soft)]">
                      Read-only access to the books. Can&apos;t change settings,
                      billing, or approve actions.
                    </span>
                  </span>
                </label>
              </div>
            </fieldset>

            <FormField
              label="Email"
              type="email"
              inputMode="email"
              required
              autoComplete="off"
              placeholder="teammate@company.co"
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              error={fieldError ?? undefined}
              data-testid="shell-settings-team-invite-email"
            />

            {/* Members get exactly one named role (named_roles — ADR-0097).
                Advisors hold the fixed `advisor` role server-side, so the
                picker is removed from the DOM under the same guard; the flip
                is announced into the page live region (selectMembership). */}
            {!isAdvisor && (
              <div data-testid="shell-settings-team-invite-roles">
                <NamedRolePicker
                  roles={roles}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  testIdPrefix="shell-settings-team-invite"
                  name="invite_org_role"
                />
              </div>
            )}

            <DialogFooter>
              <Button
                type="submit"
                variant="primary"
                size="md"
                data-testid="shell-settings-team-invite-submit"
                disabled={submitting}
              >
                {submitting ? "Creating…" : "Create invitation"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
